import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ScrollView,
  Linking,
} from 'react-native';
import LoadingAnimation from '../LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { getColors, typography, spacing, borderRadius, getShadows } from '../../styles/theme';
import { useTheme } from '../../contexts/ThemeContext';
import { getUserById } from '../../services/database';
import { getProfilePictureUrl } from '../../services/storage';

const ChatProfileSidebar = ({ user, navigation }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const [fullUserData, setFullUserData] = useState(user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchFullUserData();
    } else {
      console.warn('⚠️ ChatProfileSidebar: No user ID provided');
      setLoading(false);
    }
  }, [user?.id]);

  const fetchFullUserData = async () => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        console.error('No user ID provided');
        setLoading(false);
        return;
      }

      console.log('📥 Fetching user data for:', user.id);
      
      // Fetch user data from Supabase
      const userData = await getUserById(user.id);
      
      if (!userData) {
        console.error('User not found in database');
        setLoading(false);
        return;
      }

      console.log('✅ User data fetched:', {
        username: userData.username,
        city: userData.city,
        about: userData.about,
        rating: userData.rating,
        chesscom: userData.chesscom_username,
        lichess: userData.lichess_username,
      });

      // Convert snake_case to camelCase
      const formattedUser = {
        id: userData.id,
        username: userData.username,
        clubName: userData.club_name,
        email: userData.email,
        accountType: userData.account_type,
        profilePictureUrl: userData.profile_picture_url,
        rating: userData.rating,
        inPersonRating: userData.in_person_rating,
        wins: userData.wins,
        losses: userData.losses,
        draws: userData.draws,
        gamesPlayedInApp: userData.games_played_in_app,
        address: userData.address,
        website: userData.website,
        about: userData.about,
        latitude: userData.latitude,
        longitude: userData.longitude,
        city: userData.city,
        statusMessage: userData.status_message,
        isOnline: userData.is_online,
        lastOnlineAt: userData.last_online_at,
        chesscomUsername: userData.chesscom_username,
        chesscomBlitz: userData.chesscom_blitz,
        chesscomRapid: userData.chesscom_rapid,
        chesscomClassical: userData.chesscom_classical,
        lichessUsername: userData.lichess_username,
        lichessBlitz: userData.lichess_blitz,
        lichessRapid: userData.lichess_rapid,
        lichessClassical: userData.lichess_classical,
      };

      setFullUserData(formattedUser);
    } catch (error) {
      console.error('❌ Error fetching full user data:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        userId: user?.id,
      });
      // Keep the initial user data if fetch fails
      if (user) {
        console.log('⚠️ Using initial user data as fallback');
        setFullUserData(user);
      }
    } finally {
      setLoading(false);
    }
  };

  const displayName = fullUserData?.clubName || fullUserData?.username || 'Unknown';
  const isClub = fullUserData?.accountType === 'CLUB';

  // Calculate win rate for players
  const calculateWinRate = () => {
    if (isClub || !fullUserData) return null;
    const total = (fullUserData.wins || 0) + (fullUserData.losses || 0) + (fullUserData.draws || 0);
    if (total === 0) return null;
    return Math.round(((fullUserData.wins || 0) / total) * 100);
  };

  const winRate = calculateWinRate();

  const handleViewOnMap = () => {
    if (fullUserData?.latitude && fullUserData?.longitude) {
      navigation.navigate('Map', {
        initialRegion: {
          latitude: fullUserData.latitude,
          longitude: fullUserData.longitude,
          latitudeDelta: 0.05,
          longitudeDelta: 0.05,
        },
        selectedUserId: fullUserData.id,
      });
    }
  };

  const handleViewFullProfile = () => {
    // Navigate to UserDetail screen to view the other user's profile
    navigation.navigate('UserDetail', { userId: fullUserData.id });
  };

  const handleOpenWebsite = () => {
    if (fullUserData?.website) {
      let url = fullUserData.website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      Linking.openURL(url);
    }
  };

  const styles = getStyles(colors, isDarkMode);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!fullUserData) {
    return (
      <View style={styles.loadingContainer}>
        <Icon name="error-outline" size={48} color={colors.textSecondary} />
        <Text style={[styles.errorText, { color: colors.textSecondary }]}>
          Unable to load profile
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Profile Header */}
      <View style={styles.profileHeader}>
        <View style={styles.profileImageWrapper}>
          {fullUserData?.profilePictureUrl ? (
            <Image
              source={{ uri: getProfilePictureUrl(fullUserData.profilePictureUrl, 'medium') }}
              style={styles.profileImage}
            />
          ) : (
            <View style={[styles.profileImage, styles.profileImagePlaceholder]}>
              <Icon name={isClub ? 'business' : 'person'} size={48} color={colors.textSecondary} />
            </View>
          )}
          {/* Online Status Indicator */}
          <View style={[styles.onlineIndicator, { backgroundColor: fullUserData?.isOnline ? '#4CAF50' : '#9E9E9E' }]} />
        </View>

        <Text style={styles.displayName} numberOfLines={2}>
          {displayName}
        </Text>

        {/* Online Status Text */}
        <View style={styles.onlineStatusContainer}>
          <View style={[styles.onlineStatusDot, { backgroundColor: fullUserData?.isOnline ? '#4CAF50' : '#9E9E9E' }]} />
          <Text style={styles.onlineStatusText}>
            {fullUserData?.isOnline ? 'Online Now' : 'Offline'}
          </Text>
        </View>

        {!isClub && fullUserData?.username && (
          <Text style={styles.username}>@{fullUserData.username}</Text>
        )}

        <View style={styles.badgeContainer}>
          <View style={[styles.badge, isClub ? styles.badgeClub : styles.badgePlayer]}>
            <Icon name={isClub ? 'business' : 'person'} size={14} color={colors.textInverse} />
            <Text style={styles.badgeText}>{isClub ? 'Club' : 'Player'}</Text>
          </View>
        </View>

        {/* City - Always show section */}
        <View style={styles.cityContainer}>
          <Icon name="location-city" size={16} color={colors.textSecondary} />
          <Text style={styles.cityText}>{fullUserData?.city || 'No city set'}</Text>
        </View>

        {/* Status Message */}
        {fullUserData?.statusMessage && (
          <View style={styles.statusContainer}>
            <Text style={styles.statusText}>"{fullUserData.statusMessage}"</Text>
          </View>
        )}
      </View>

      {/* Rating Section - Always show for players */}
      {!isClub && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Rating</Text>
          <View style={styles.statItem}>
            <Icon name="stars" size={20} color={colors.primary} />
            <Text style={styles.statLabel}>In-App Rating</Text>
            <Text style={styles.statValue}>{fullUserData?.rating || 1200}</Text>
          </View>
        </View>
      )}

      {/* Connected Accounts Section - Always show for players, show if connected */}
      {!isClub && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Connected Accounts</Text>
          
          {fullUserData?.chesscomUsername || fullUserData?.lichessUsername ? (
            <>
              {/* Chess.com */}
              {fullUserData?.chesscomUsername && (
                <View style={styles.platformContainer}>
                  <View style={styles.platformHeader}>
                    <Text style={styles.platformIcon}>♟️</Text>
                    <View style={styles.platformInfo}>
                      <Text style={styles.platformName}>Chess.com</Text>
                      <Text style={styles.platformUsername}>@{fullUserData.chesscomUsername}</Text>
                    </View>
                  </View>
                  {(fullUserData.chesscomBlitz || fullUserData.chesscomRapid || fullUserData.chesscomClassical) && (
                    <View style={styles.ratingsRow}>
                      {fullUserData.chesscomBlitz && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Blitz</Text>
                          <Text style={styles.ratingValue}>{fullUserData.chesscomBlitz}</Text>
                        </View>
                      )}
                      {fullUserData.chesscomRapid && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Rapid</Text>
                          <Text style={styles.ratingValue}>{fullUserData.chesscomRapid}</Text>
                        </View>
                      )}
                      {fullUserData.chesscomClassical && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Classical</Text>
                          <Text style={styles.ratingValue}>{fullUserData.chesscomClassical}</Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              )}

              {/* Lichess */}
              {fullUserData?.lichessUsername && (
                <View style={styles.platformContainer}>
                  <View style={styles.platformHeader}>
                    <Text style={styles.platformIcon}>♞</Text>
                    <View style={styles.platformInfo}>
                      <Text style={styles.platformName}>Lichess</Text>
                      <Text style={styles.platformUsername}>@{fullUserData.lichessUsername}</Text>
                    </View>
                  </View>
                  {(fullUserData.lichessBlitz || fullUserData.lichessRapid || fullUserData.lichessClassical) && (
                    <View style={styles.ratingsRow}>
                      {fullUserData.lichessBlitz && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Blitz</Text>
                          <Text style={styles.ratingValue}>{fullUserData.lichessBlitz}</Text>
                        </View>
                      )}
                      {fullUserData.lichessRapid && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Rapid</Text>
                          <Text style={styles.ratingValue}>{fullUserData.lichessRapid}</Text>
                        </View>
                      )}
                      {fullUserData.lichessClassical && (
                        <View style={styles.ratingBadge}>
                          <Text style={styles.ratingLabel}>Classical</Text>
                          <Text style={styles.ratingValue}>{fullUserData.lichessClassical}</Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              )}
            </>
          ) : (
            <Text style={styles.noAccountsText}>No connected accounts</Text>
          )}
        </View>
      )}

      {/* Bio/About Section - Always show */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Bio</Text>
        {fullUserData?.about ? (
          <Text style={styles.aboutText}>{fullUserData.about}</Text>
        ) : (
          <Text style={styles.noBioText}>No bio available</Text>
        )}
      </View>

      {/* Location Section */}
      {fullUserData?.address && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Location</Text>
          <View style={styles.infoItem}>
            <Icon name="location-on" size={20} color={colors.primary} />
            <Text style={styles.infoText}>{fullUserData.address}</Text>
          </View>
        </View>
      )}

      {/* Website Section (for clubs) */}
      {isClub && fullUserData?.website && (
        <View style={styles.section}>
          <TouchableOpacity style={styles.linkItem} onPress={handleOpenWebsite}>
            <Icon name="language" size={20} color={colors.primary} />
            <Text style={styles.linkText} numberOfLines={1}>
              {fullUserData.website}
            </Text>
            <Icon name="open-in-new" size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Actions */}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.actionButton} onPress={handleViewFullProfile}>
          <Icon name="person-outline" size={20} color={colors.primary} />
          <Text style={styles.actionButtonText}>View Full Profile</Text>
        </TouchableOpacity>

        {fullUserData?.latitude && fullUserData?.longitude && (
          <TouchableOpacity style={styles.actionButton} onPress={handleViewOnMap}>
            <Icon name="map" size={20} color={colors.primary} />
            <Text style={styles.actionButtonText}>View on Map</Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );
};

const getStyles = (colors, isDarkMode) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  content: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
  },
  errorText: {
    fontSize: typography.body,
    marginTop: spacing.sm,
    textAlign: 'center',
  },
  profileHeader: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  profileImageWrapper: {
    position: 'relative',
    marginBottom: spacing.md,
  },
  profileImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  profileImagePlaceholder: {
    backgroundColor: colors.backgroundDark,
    justifyContent: 'center',
    alignItems: 'center',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 3,
    borderColor: colors.surface,
  },
  onlineStatusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  onlineStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  onlineStatusText: {
    fontSize: typography.caption,
    color: colors.textSecondary,
  },
  cityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  cityText: {
    fontSize: typography.body,
    color: colors.textSecondary,
  },
  statusContainer: {
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundDark,
    borderRadius: borderRadius.sm,
    maxWidth: '90%',
  },
  statusText: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  displayName: {
    fontSize: typography.h4,
    fontWeight: typography.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  username: {
    fontSize: typography.body,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  badgeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.pill,
    gap: spacing.xs,
  },
  badgePlayer: {
    backgroundColor: colors.primary,
  },
  badgeClub: {
    backgroundColor: colors.club,
  },
  badgeText: {
    fontSize: typography.caption,
    fontWeight: typography.semiBold,
    color: colors.textInverse,
  },
  section: {
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  sectionTitle: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    color: colors.text,
    marginBottom: spacing.sm,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  statLabel: {
    flex: 1,
    fontSize: typography.body,
    color: colors.text,
    marginLeft: spacing.sm,
  },
  statValue: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.text,
  },
  aboutText: {
    fontSize: typography.body,
    color: colors.textSecondary,
    lineHeight: typography.body * typography.lineHeightNormal,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  infoText: {
    flex: 1,
    fontSize: typography.body,
    color: colors.textSecondary,
    lineHeight: typography.body * typography.lineHeightNormal,
  },
  linkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  linkText: {
    flex: 1,
    fontSize: typography.body,
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  actions: {
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.sm,
  },
  actionButtonText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.primary,
  },
  recordContainer: {
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  recordText: {
    fontSize: typography.body,
    color: colors.textSecondary,
    fontWeight: typography.medium,
  },
  platformContainer: {
    marginBottom: spacing.md,
    padding: spacing.sm,
    backgroundColor: colors.backgroundDark,
    borderRadius: borderRadius.sm,
  },
  platformHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    gap: spacing.sm,
  },
  platformIcon: {
    fontSize: 20,
  },
  platformInfo: {
    flex: 1,
  },
  platformName: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.text,
  },
  platformUsername: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  ratingsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  ratingBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  ratingLabel: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    marginBottom: 2,
  },
  ratingValue: {
    fontSize: typography.bodySmall,
    fontWeight: typography.semiBold,
    color: colors.text,
  },
  noAccountsText: {
    fontSize: typography.body,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  noBioText: {
    fontSize: typography.body,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
});

export default ChatProfileSidebar;

